import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import moment from 'moment';
import { MapPin } from 'lucide-react';
import { useScrollVisibility } from '../../../hooks/useScrollVisibility';
import { useCalendarDragDrop, RescheduleRequest } from '../hooks/useCalendarDragDrop';
import { ScheduleItem, TimeBox, Event } from '../../../types';
import { Task } from '../../../types/task';
import { IdealWeek } from '../../../types/user';
import { ChevronDown, ChevronUp } from '../../../components/common/icons';
import { logger } from '../../../utils/logger';
import { AllDayEventChip, getVisibleAllDayEvents } from './AllDayEventsSection';
import PlacementPreview from '../../../components/calendar/PlannedLayer/PlacementPreview';
import {
  generateHours,
  generateDateRange,
  getScheduleItemsForDay,
  calculateScheduleItemPosition,
  processOverlappingScheduleItems,
  separateAllDayAndTimedItems,
  getItemDisplayTitle,
  formatTimeRange,
  getEventStyleClasses,
  getTimeBoxStyleClasses,
  CARD_SPACING,
  SCROLL_CONFIG,
  DISPLAY_CONFIG,
  DRAG_CONFIG,
  scrollToCurrentTime,
} from '../../../utils/calendarHelper';
import useSwipeGesture from '../../../hooks/useSwipeGesture';
import DateRangeButton from '../../../components/common/DateRangeButton';
import TimeColumn from './TimeColumn';

interface ThreeDayViewProps {
  date: Date;
  scheduleItems: ScheduleItem[];
  onSelectTimeBox: (item: ScheduleItem) => void;
  onSelectSlot: (start: Date, end: Date) => void;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onNavigateToday: () => void;
  onDateSelect: (date: Date) => void;
  onReschedule?: (request: RescheduleRequest) => Promise<void>;
  /** 放置模式狀態（行動版） */
  placementMode?: {
    state: 'idle' | 'selecting' | 'previewing';
    selectedTask: Task | null;
    previewSlot: { startTime: Date; endTime: Date } | null;
  };
  /** 選擇時段回調（放置模式專用） */
  onSelectTimeSlotForPlacement?: (startTime: Date, endTime: Date) => void;
  /** 確認放置回調（放置模式專用） */
  onConfirmPlacement?: () => void;
  /** Ideal Week 主題日設定 */
  idealWeek?: IdealWeek;
}

// 移除重複的輔助函數，統一使用 calendarHelper 中的函數

const ThreeDayView: React.FC<ThreeDayViewProps> = ({
  date,
  scheduleItems,
  onSelectTimeBox,
  onSelectSlot,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onDateSelect,
  onReschedule,
  placementMode,
  onSelectTimeSlotForPlacement,
  onConfirmPlacement,
  idealWeek
}) => {
  // 全天事件展開/收合狀態
  const [isAllDayExpanded, setIsAllDayExpanded] = useState(false);

  // ideal week title 展開狀態（以 day key 為識別）
  const [expandedThemeDays, setExpandedThemeDays] = useState<Set<string>>(new Set());

  const toggleThemeDay = useCallback((dayKey: string) => {
    setExpandedThemeDays(prev => {
      const next = new Set(prev);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  }, []);

  // 快取今日日期以優化效能
  const today = useMemo(() => {
    const now = new Date();
    return { date: now.getDate(), month: now.getMonth(), year: now.getFullYear() };
  }, []);

  // 滾動顯示/隱藏工具欄
  const isToolbarVisible = useScrollVisibility(50);

  // 時間格區域的 ref（用於拖拽）
  const timeGridContainerRef = useRef<HTMLDivElement>(null);

  // 三日視圖特定配置
  const DAYS_TO_SHOW = 3;

  const hours = generateHours(DISPLAY_CONFIG.START_HOUR, DISPLAY_CONFIG.END_HOUR);

  // 生成 9 天的日期（3 個區間：prev + current + next）
  const prevDays = useMemo(() => {
    const prevStartDate = new Date(date);
    prevStartDate.setDate(date.getDate() - DAYS_TO_SHOW);
    return generateDateRange(prevStartDate, DAYS_TO_SHOW);
  }, [date]);

  const currentDays = useMemo(() => {
    return generateDateRange(date, DAYS_TO_SHOW);
  }, [date]);

  const nextDays = useMemo(() => {
    const nextStartDate = new Date(date);
    nextStartDate.setDate(date.getDate() + DAYS_TO_SHOW);
    return generateDateRange(nextStartDate, DAYS_TO_SHOW);
  }, [date]);

  // 保留 days 變數以保持向後相容（指向 currentDays）
  const days = currentDays;

  // 使用拖拽 Hook（支援滑鼠和觸控）
  const {
    dragState,
    isDragging,
    isLongPressing,
    handleMouseDown,
    handleTouchStart,
    getDragPreviewStyle,
    getCursorStyle,
    getCurrentDayIndex,
  } = useCalendarDragDrop({
    hourHeight: DISPLAY_CONFIG.HOUR_HEIGHT,
    startHour: DISPLAY_CONFIG.START_HOUR,
    endHour: DISPLAY_CONFIG.END_HOUR,
    timeColumnWidth: DISPLAY_CONFIG.TIME_COLUMN_WIDTH,
    days, // 傳入日期列以支援跨日期拖動
    onReschedule,
    enabled: !!onReschedule,
  });

  // 計算動態閾值（螢幕寬度的 20%）
  const swipeThreshold = useMemo(() => {
    return typeof window !== 'undefined' ? window.innerWidth * 0.2 : 80;
  }, []);

  // 滑動手勢功能（拖拽時禁用，isAnimating 由 hook 內部處理）
  const swipeGesture = useSwipeGesture({
    onSwipeLeft: onNavigateNext,  // 向左滑 = 前進3天
    onSwipeRight: onNavigatePrev, // 向右滑 = 後退3天
    threshold: swipeThreshold,
    enabled: !isDragging // 拖拽時禁用滑動
  });

  // processedDays 已移除 - 處理邏輯已整合到 renderDayGroup 函數內

  // ref 用於時間區域的滾動和固定標頭
  const timeGridRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // 移除 showFloatingHeader state - 改用固定表頭架構

  // 處理卡片點擊（區分拖拽和點擊）
  const handleItemClick = useCallback((item: ScheduleItem) => {
    // 如果正在滑動或拖拽，不觸發點擊事件
    if (swipeGesture.isSwiping || Math.abs(swipeGesture.swipeDistance) > 5 || isDragging) {
      return;
    }
    onSelectTimeBox(item);
  }, [swipeGesture.isSwiping, swipeGesture.swipeDistance, isDragging, onSelectTimeBox]);

  // 處理卡片觸控開始（支援拖拽）
  const handleCardTouchStart = useCallback(
    (e: React.TouchEvent, item: ScheduleItem, dayIndex: number) => {
      if (!timeGridContainerRef.current || !onReschedule) return;
      const containerRect = timeGridContainerRef.current.getBoundingClientRect();
      handleTouchStart(e, item, containerRect, dayIndex);
    },
    [handleTouchStart, onReschedule]
  );



  // 切換日期時清空 ideal week title 展開狀態
  useEffect(() => {
    setExpandedThemeDays(new Set());
  }, [date.getTime()]);

  // 自動滾動到當前時間（只在今天在當前 3 天範圍內時才滾動）
  useEffect(() => {
    const timer = setTimeout(() => {
      // 檢查今天是否在當前 3 天的日期範圍內
      const now = new Date();
      const todayDateString = now.toDateString();
      const isTodayInCurrentDays = days.some(day => day.toDateString() === todayDateString);

      // 只在今天在當前 3 天時才滾動
      if (isTodayInCurrentDays && timeGridRef.current) {
        scrollToCurrentTime(timeGridRef.current, DISPLAY_CONFIG.START_HOUR, DISPLAY_CONFIG.HOUR_HEIGHT);
      }
    }, SCROLL_CONFIG.INITIAL_DELAY);

    return () => clearTimeout(timer);
  }, [date.getTime()]);


  /**
   * 渲染單一日期區間（3天）的時間格和事件
   * @param dayGroup - 日期陣列（3天）
   * @param groupIndex - 區間索引（0=prev, 1=current, 2=next）
   */
  const renderDayGroup = useCallback((dayGroup: Date[], groupIndex: number) => {
    // 處理這個區間的資料
    const groupProcessedDays = dayGroup.map(day => {
      const dayScheduleItems = getScheduleItemsForDay(scheduleItems, day);
      const { allDayEvents, timedItems: timedScheduleItems } = separateAllDayAndTimedItems(dayScheduleItems);
      const processedTimedItems = processOverlappingScheduleItems(timedScheduleItems, DISPLAY_CONFIG.START_HOUR, DISPLAY_CONFIG.END_HOUR);

      // Debug logging
      if (allDayEvents.length > 0) {
        logger.dev('[ThreeDayView] 檢測到全天事件:', {
          date: day.toDateString(),
          allDayEvents: allDayEvents.map(e => {
            const event = e as Event;
            return {
              title: event.title,
              timeType: event.timeType,
              startTime: event.startTime,
              endTime: event.endTime
            };
          })
        });
      }

      return {
        date: day,
        allDayEvents,
        timedItems: processedTimedItems
      };
    });

    return (
      <div
        key={`group-${groupIndex}`}
        className="flex-shrink-0 relative"
        style={{ width: '33.333%' }}
      >
        {/* 時間格線 - 15 分鐘細分 */}
        {hours.map(hour => (
          <div key={hour} className="border-b border-gray-100" style={{ height: `${DISPLAY_CONFIG.HOUR_HEIGHT}px` }}>
            <div className="flex flex-1">
              {dayGroup.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className="flex-1 border-r border-gray-200 last:border-r-0 flex flex-col"
                >
                  {/* 4 個 15 分鐘的子格子 */}
                  {[0, 15, 30, 45].map((minute, idx) => (
                    <div
                      key={minute}
                      className={`relative cursor-pointer
                        hover:border-2 hover:border-dashed hover:border-sky-300
                        hover:bg-sky-500/5 hover:rounded-md hover:z-10
                        transition-all duration-200 ${idx < 3 ? 'border-b border-gray-50' : ''}`}
                      style={{ height: `${DISPLAY_CONFIG.HOUR_HEIGHT / 4}px` }}
                      onClick={(e) => {
                        // 如果正在滑動，不觸發點擊事件
                        if (swipeGesture.isSwiping || Math.abs(swipeGesture.swipeDistance) > 5) {
                          return;
                        }

                        // 放置模式：計算基於任務 estimatedTime 的時段
                        if (placementMode?.state === 'selecting' && onSelectTimeSlotForPlacement) {
                          const start = new Date(day);
                          start.setHours(hour, minute, 0, 0);

                          // 計算結束時間（基於任務預估時長，預設 60 分鐘）
                          const durationMinutes = placementMode.selectedTask?.estimatedTime || 60;
                          const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

                          onSelectTimeSlotForPlacement(start, end);
                          return;
                        }

                        // 一般模式：使用 15 分鐘固定時長
                        const start = new Date(day);
                        start.setHours(hour, minute, 0, 0);
                        const end = new Date(start);
                        end.setTime(start.getTime() + 15 * 60 * 1000); // 15 分鐘
                        onSelectSlot(start, end);
                      }}
                    ></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 事件卡片容器層 - 使用 pointer-events: none 讓點擊穿透到時間格 */}
        <div className="absolute inset-0 pointer-events-none">
          {/* 渲染這個區間的定時事件 */}
          {groupProcessedDays.map((day, dayIndex) => {
            return day.timedItems.map((item, itemIndex) => {
            const hour = item.startTime.getHours();
            if (hour < DISPLAY_CONFIG.START_HOUR || hour > DISPLAY_CONFIG.END_HOUR) return null;

            const { top, height: originalHeight } = calculateScheduleItemPosition(
              item as ScheduleItem,
              DISPLAY_CONFIG.HOUR_HEIGHT,
              DISPLAY_CONFIG.START_HOUR
            );

            const height = Math.max(originalHeight, DISPLAY_CONFIG.MIN_TIMEBOX_HEIGHT);
            const columnCount = dayGroup.length;
            const containerWidth = '100%'; // 區間內部是 100%
            const left = `calc(${containerWidth} * ${dayIndex} / ${columnCount})`;

            const marginPx = CARD_SPACING.margin;
            const verticalGap = CARD_SPACING.vertical;

            const positionWidth = item.totalPositions > 1
              ? `calc((${containerWidth} / ${columnCount} - ${marginPx * 2}px) / ${item.totalPositions})`
              : `calc(${containerWidth} / ${columnCount} - ${marginPx * 2}px)`;

            const positionOffset = item.totalPositions > 1
              ? `calc((${containerWidth} / ${columnCount} - ${marginPx * 2}px) * ${item.position} / ${item.totalPositions})`
              : '0px';

            const styleClasses = item.itemType === 'timebox'
              ? getTimeBoxStyleClasses(item as TimeBox)
              : getEventStyleClasses(item as Event);

            // 檢查拖拽狀態（只在當前區間 groupIndex === 1 才處理拖拽）
            const isBeingDragged = groupIndex === 1 && dragState?.itemId === item.id;
            const isDraggedToOtherDay = isBeingDragged &&
              dragState?.currentDayIndex !== undefined &&
              dragState?.currentDayIndex !== dayIndex;

            let displayTop: string;
            let displayHeight: string;
            let displayLeft: string;
            let displayWidth: string;
            let displayOpacity: number;
            let displayZIndex: number | undefined;
            let displayBoxShadow: string | undefined;
            let displayCursor: string;
            let displayTransform: string | undefined;

            if (isDraggedToOtherDay) {
              displayTop = `${top + verticalGap}px`;
              displayHeight = `${height - verticalGap}px`;
              displayLeft = `calc(${left} + ${positionOffset})`;
              displayWidth = positionWidth;
              displayOpacity = 0.3;
              displayZIndex = undefined;
              displayBoxShadow = undefined;
              displayCursor = 'default';
            } else if (isBeingDragged) {
              const dragPreviewStyle = getDragPreviewStyle(item as ScheduleItem, dayIndex);
              displayTop = dragPreviewStyle ? dragPreviewStyle.top as string : `${top + verticalGap}px`;
              displayHeight = dragPreviewStyle ? dragPreviewStyle.height as string : `${height - verticalGap}px`;
              displayLeft = `calc(${left} + ${positionOffset})`;
              displayWidth = positionWidth;
              displayOpacity = 0.9;
              displayZIndex = undefined;
              displayBoxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              displayCursor = 'grabbing';
            } else if (groupIndex === 1 && isLongPressing && dragState?.itemId === item.id) {
              displayTop = `${top + verticalGap}px`;
              displayHeight = `${height - verticalGap}px`;
              displayLeft = `calc(${left} + ${positionOffset})`;
              displayWidth = positionWidth;
              displayOpacity = 1;
              displayZIndex = 100;
              displayBoxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
              displayCursor = 'grabbing';
              displayTransform = 'scale(1.05) translateY(-4px)';
            } else {
              displayTop = `${top + verticalGap}px`;
              displayHeight = `${height - verticalGap}px`;
              displayLeft = `calc(${left} + ${positionOffset})`;
              displayWidth = positionWidth;
              displayOpacity = 1;
              displayZIndex = undefined;
              displayBoxShadow = undefined;
              displayCursor = onReschedule ? 'grab' : 'pointer';
            }

            return (
              <div
                key={`${item.itemType}-${groupIndex}-${dayIndex}-${itemIndex}`}
                className={`absolute px-2 py-1 overflow-hidden ${styleClasses} ${onReschedule ? 'select-none' : 'cursor-pointer'
                  } ${isBeingDragged && !isDraggedToOtherDay ? 'ring-2 ring-sky-400 ring-opacity-50 z-drag-active' : ''}`}
                style={{
                  top: displayTop,
                  left: displayLeft,
                  height: displayHeight,
                  width: displayWidth,
                  marginLeft: `${marginPx}px`,
                  marginRight: `${marginPx}px`,
                  borderRadius: `${CARD_SPACING.borderRadius}px`,
                  opacity: displayOpacity,
                  zIndex: displayZIndex,
                  boxShadow: displayBoxShadow,
                  transform: displayTransform,
                  transition: isBeingDragged ? 'none' : (isLongPressing && dragState?.itemId === item.id ? 'transform 150ms ease-out, box-shadow 150ms ease-out' : 'box-shadow 0.2s'),
                  touchAction: isBeingDragged ? 'none' : 'auto',
                  cursor: displayCursor,
                  pointerEvents: 'auto', // 確保事件卡片可以接收點擊事件
                }}
                onClick={() => handleItemClick(item as ScheduleItem)}
                onTouchStart={(e) => groupIndex === 1 ? handleCardTouchStart(e, item as ScheduleItem, dayIndex) : undefined}
              >
                <div className="text-xs font-medium truncate">{getItemDisplayTitle(item as ScheduleItem)}</div>
                <div className="text-xs text-gray-600">
                  {isBeingDragged && dragState && !isDraggedToOtherDay ? (
                    `${moment(dragState.currentStartTime).format('HH:mm')} - ${moment(dragState.currentEndTime).format('HH:mm')}`
                  ) : (
                    formatTimeRange(item as ScheduleItem)
                  )}
                </div>
                {item.location && (
                  <div className="text-xs text-gray-600 truncate flex items-center gap-1">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="truncate">{item.location}</span>
                  </div>
                )}
                {item.itemType === 'timebox' && item.taskId && (
                  <div className="mt-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-sky-100 text-sky-800">
                      Task
                    </span>
                  </div>
                )}
              </div>
            );
          });
        })}
        </div>
        {/* 結束：事件卡片容器層 */}
      </div>
    );
  }, [scheduleItems, hours, today, swipeGesture.isSwiping, swipeGesture.swipeDistance, dragState, isDragging, isLongPressing, onReschedule, onSelectSlot, handleItemClick, handleCardTouchStart, getDragPreviewStyle]);

  // 渲染當前時間指示線（適配 3 區間結構）
  const renderCurrentTimeIndicator = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const todayDateString = now.toDateString();

    // 找出今天在哪個區間
    let groupIndex = -1;
    let dayIndexInGroup = -1;

    if (prevDays.some((day, idx) => {
      if (day.toDateString() === todayDateString) {
        dayIndexInGroup = idx;
        return true;
      }
      return false;
    })) {
      groupIndex = 0; // prev 區間
    } else if (currentDays.some((day, idx) => {
      if (day.toDateString() === todayDateString) {
        dayIndexInGroup = idx;
        return true;
      }
      return false;
    })) {
      groupIndex = 1; // current 區間
    } else if (nextDays.some((day, idx) => {
      if (day.toDateString() === todayDateString) {
        dayIndexInGroup = idx;
        return true;
      }
      return false;
    })) {
      groupIndex = 2; // next 區間
    }

    // 如果今天不在任何區間，不顯示指示線
    if (groupIndex === -1 || dayIndexInGroup === -1) {
      return null;
    }

    // 計算指示線的垂直位置
    const top = DISPLAY_CONFIG.HOUR_HEIGHT * (currentHour - DISPLAY_CONFIG.START_HOUR) +
      (currentMinute / 60) * DISPLAY_CONFIG.HOUR_HEIGHT;

    // 計算水平位置（在滑動容器內的絕對位置）
    // 每個區間佔 33.333% 的寬度，區間內有 3 天
    const groupWidthPercent = 33.333;
    const dayWidthPercent = groupWidthPercent / 3;
    const leftPercent = groupIndex * groupWidthPercent + dayIndexInGroup * dayWidthPercent;

    return (
      <div
        className="absolute flex items-center z-floating pointer-events-none"
        style={{
          top: `${top}px`,
          left: `${leftPercent}%`,
          width: `${dayWidthPercent}%`
        }}
      >
        <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
        <div className="flex-1 h-0.5 bg-red-500"></div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full relative">
      {/* 工具欄 - 固定不動 */}
      <div className={`
        flex items-center justify-between py-3 px-0
        border-b border-[var(--border-default)] bg-surface-page
        sticky top-0 z-toolbar
        transition-transform duration-normal ease-in-out
        ${isToolbarVisible ? 'translate-y-0' : '-translate-y-full'}
      `}>
        <div className="pl-2">
          <DateRangeButton
            dateText={moment(date).format('MMM YYYY')}
            currentDate={date}
            onDateSelect={onDateSelect}
            isMobile={true}
          />
        </div>

        <div className="flex items-center space-x-2 mr-4">
          <button
            onClick={onNavigatePrev}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          <button
            onClick={onNavigateToday}
            className="px-3 py-1.5 bg-sky-100 text-sky-700 rounded-md hover:bg-sky-200 transition-colors text-sm font-medium"
          >
            Today
          </button>

          <button
            onClick={onNavigateNext}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Sticky 表頭層：Time + 日期標頭（跟隨滑動）*/}
      <div ref={headerRef} className={`
        flex-shrink-0 flex border-b border-[var(--border-default)] w-full bg-surface-page
        sticky z-sticky
        transition-[top] duration-normal ease-in-out
        ${isToolbarVisible ? 'top-16' : 'top-0'}
      `}>
        {/* 左側：Time 標籤（固定）*/}
        <div
          style={{ width: `${DISPLAY_CONFIG.TIME_COLUMN_WIDTH}px`, minWidth: `${DISPLAY_CONFIG.TIME_COLUMN_WIDTH}px` }}
          className="text-center py-2 font-medium text-gray-500 border-r border-gray-200 flex-shrink-0"
        >
          Time
        </div>

        {/* 右側：日期標頭容器（跟隨滑動）*/}
        <div className="flex-1 overflow-hidden">
          <div
            style={{
              width: '300%',
              display: 'flex',
              transform: `translateX(calc(-33.333% + ${swipeGesture.swipeDistance}px))`,
              transition: (swipeGesture.isSwiping || swipeGesture.isResetting) ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            {/* 渲染 3 個區間的日期標頭 */}
            {[prevDays, currentDays, nextDays].map((dayGroup, groupIdx) => (
              <div key={`header-${groupIdx}`} style={{ width: '33.333%' }} className="flex">
                {dayGroup.map((day, dayIdx) => {
                  const theme = idealWeek?.[day.getDay() as keyof IdealWeek];
                  const isToday = day.getDate() === today.date && day.getMonth() === today.month && day.getFullYear() === today.year;
                  const dayKey = `${groupIdx}-${dayIdx}`;
                  const isThemeExpanded = expandedThemeDays.has(dayKey);
                  return (
                    <div
                      key={dayIdx}
                      className="flex-1 text-center py-2"
                    >
                      <div className="flex items-center justify-center gap-1 font-medium text-gray-500">
                        <span>{moment(day).format('ddd')}</span>
                        {isToday ? (
                          <span aria-current="date" className="inline-flex items-center justify-center w-7 h-7 bg-sky-500 text-white font-semibold rounded-full text-sm">
                            {day.getDate()}
                          </span>
                        ) : (
                          <span>{day.getDate()}</span>
                        )}
                      </div>
                      {theme && (
                        <button
                          className="w-full px-1"
                          onClick={() => toggleThemeDay(dayKey)}
                          aria-expanded={isThemeExpanded}
                          aria-label={isThemeExpanded ? 'Collapse theme' : 'Expand theme'}
                        >
                          {isThemeExpanded ? (
                            <div className="text-xs text-sky-500 font-normal leading-snug">
                              <div className="text-center">{theme.title}</div>
                              {theme.description && (
                                <div className="text-gray-400 text-left line-clamp-2">{theme.description}</div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-sky-500 font-normal truncate text-center">
                              {theme.title}
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 全天事件區域 - Sticky 固定在日期標頭下方（跟隨滑動）*/}
      <div className={`
        flex-shrink-0 bg-gray-50/80 border-b border-gray-200
        sticky z-[39]
        transition-[top] duration-normal ease-in-out
        ${isToolbarVisible ? 'top-[104px]' : 'top-[40px]'}
      `}>
        {/* 只檢查當前顯示的 3 天是否有全天事件 */}
        {(() => {
          const hasAllDayEventsInCurrentView = currentDays.some(day => {
            const dayScheduleItems = getScheduleItemsForDay(scheduleItems, day);
            const { allDayEvents } = separateAllDayAndTimedItems(dayScheduleItems);
            return allDayEvents.length > 0;
          });

          if (!hasAllDayEventsInCurrentView) return null;

          // 檢查是否需要 toggle（任一天超過 3 筆）
          const needsToggle = currentDays.some(day => {
            const dayScheduleItems = getScheduleItemsForDay(scheduleItems, day);
            const { allDayEvents } = separateAllDayAndTimedItems(dayScheduleItems);
            return allDayEvents.length > 3;
          });

          return (
            <div className="flex" role="region" aria-label="All-day events">
              {/* 左側：Toggle 圖示（固定不滑動）*/}
              <div
                style={{ width: `${DISPLAY_CONFIG.TIME_COLUMN_WIDTH}px`, minWidth: `${DISPLAY_CONFIG.TIME_COLUMN_WIDTH}px` }}
                className="flex-shrink-0 border-r border-gray-200 flex items-center justify-center"
              >
                {needsToggle && (
                  <button
                    onClick={() => setIsAllDayExpanded(prev => !prev)}
                    className="flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors w-full h-full min-h-[32px]"
                    aria-label={isAllDayExpanded ? 'Collapse all-day events' : 'Expand all-day events'}
                    aria-expanded={isAllDayExpanded}
                  >
                    {isAllDayExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
              </div>

              {/* 右側：全天事件容器（跟隨滑動）*/}
              <div className="flex-1 overflow-hidden">
                <div
                  style={{
                    width: '300%',
                    display: 'flex',
                    transform: `translateX(calc(-33.333% + ${swipeGesture.swipeDistance}px))`,
                    transition: (swipeGesture.isSwiping || swipeGesture.isResetting) ? 'none' : 'transform 0.3s ease-out',
                  }}
                >
                  {/* 渲染 3 個區間的全天事件 */}
                  {[prevDays, currentDays, nextDays].map((dayGroup, groupIdx) => (
                    <div key={`allday-${groupIdx}`} style={{ width: '33.333%' }} className="flex">
                      {dayGroup.map((day, dayIdx) => {
                        const dayScheduleItems = getScheduleItemsForDay(scheduleItems, day);
                        const { allDayEvents } = separateAllDayAndTimedItems(dayScheduleItems);
                        const { visibleEvents, hiddenCount } = getVisibleAllDayEvents(
                          allDayEvents as Event[],
                          3,
                          isAllDayExpanded
                        );

                        return (
                          <div key={dayIdx} className="flex-1 flex flex-col gap-0.5 py-0.5 px-0.5">
                            {visibleEvents.map((event) => (
                              <AllDayEventChip
                                key={event.id}
                                event={event}
                                onSelect={(e) => handleItemClick(e)}
                              />
                            ))}
                            {hiddenCount > 0 && (
                              <div
                                className="text-xs text-gray-500 px-1.5 leading-5 cursor-pointer hover:text-gray-700 transition-colors"
                                onClick={() => setIsAllDayExpanded(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setIsAllDayExpanded(true);
                                  }
                                }}
                                aria-label={`${hiddenCount} more all-day events, click to expand`}
                              >
                                +{hiddenCount} more
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* 時間格和時間箱 - 可滾動部分 */}
      {/* 底部 padding 避免被 Tasks candy bar (56px) + 底部導航 (64px) 遮擋 */}
      <div className="flex-1 overflow-y-auto hide-scrollbar pb-32" ref={timeGridRef}>
        {/* flex-row 佈局：左側固定時間欄 + 右側滑動容器 */}
        <div className="flex flex-row relative" ref={timeGridContainerRef}>
          {/* 左側：固定時間欄 */}
          <TimeColumn
            hours={hours}
            hourHeight={DISPLAY_CONFIG.HOUR_HEIGHT}
            timeColumnWidth={DISPLAY_CONFIG.TIME_COLUMN_WIDTH}
          />

          {/* 右側：滑動容器外層（overflow-hidden 裁切區）*/}
          <div className="flex-1 overflow-hidden relative">
            {/* 滑動內容容器（300% 寬度，初始位置 translateX(-33.333%)）*/}
            <div
              ref={swipeGesture.swipeRef}
              className="h-full relative"
              style={{
                width: '300%',
                display: 'flex',
                transform: `translateX(calc(-33.333% + ${swipeGesture.swipeDistance}px))`,
                transition: (swipeGesture.isSwiping || swipeGesture.isResetting) ? 'none' : 'transform 0.3s ease-out',
              }}
            >
              {/* 渲染 3 個區間：prev, current, next */}
              {renderDayGroup(prevDays, 0)}
              {renderDayGroup(currentDays, 1)}
              {renderDayGroup(nextDays, 2)}

              {/* 當前時間指示線 */}
              {renderCurrentTimeIndicator()}

              {/* 放置模式預覽方塊 */}
              {placementMode?.state === 'previewing' &&
                placementMode.selectedTask &&
                placementMode.previewSlot &&
                onConfirmPlacement &&
                (() => {
                  const { startTime, endTime } = placementMode.previewSlot;

                  // 找出預覽時段所在的日期在哪個區間和哪一天
                  const previewDateString = startTime.toDateString();
                  let groupIndex = -1;
                  let dayIndexInGroup = -1;

                  // 檢查在哪個區間（prev=0, current=1, next=2）
                  if (prevDays.some((day, idx) => {
                    if (day.toDateString() === previewDateString) {
                      dayIndexInGroup = idx;
                      return true;
                    }
                    return false;
                  })) {
                    groupIndex = 0;
                  } else if (currentDays.some((day, idx) => {
                    if (day.toDateString() === previewDateString) {
                      dayIndexInGroup = idx;
                      return true;
                    }
                    return false;
                  })) {
                    groupIndex = 1;
                  } else if (nextDays.some((day, idx) => {
                    if (day.toDateString() === previewDateString) {
                      dayIndexInGroup = idx;
                      return true;
                    }
                    return false;
                  })) {
                    groupIndex = 2;
                  }

                  // 如果日期不在可見範圍，不渲染
                  if (groupIndex === -1 || dayIndexInGroup === -1) {
                    return null;
                  }

                  // 計算垂直位置（基於時間）
                  const startHour = startTime.getHours();
                  const startMinute = startTime.getMinutes();
                  const endHour = endTime.getHours();
                  const endMinute = endTime.getMinutes();

                  const top =
                    (startHour - DISPLAY_CONFIG.START_HOUR) * DISPLAY_CONFIG.HOUR_HEIGHT +
                    (startMinute / 60) * DISPLAY_CONFIG.HOUR_HEIGHT;

                  const height =
                    (endHour - startHour) * DISPLAY_CONFIG.HOUR_HEIGHT +
                    ((endMinute - startMinute) / 60) * DISPLAY_CONFIG.HOUR_HEIGHT;

                  // 使用百分比定位（相對於 300% 寬度的滑動容器）
                  // 每個區間佔 33.333%，每天佔 33.333% / 3 = 11.111%
                  const groupWidthPercent = 100 / 3;  // 33.333%
                  const dayWidthPercent = groupWidthPercent / DAYS_TO_SHOW; // 11.111%
                  const leftPercent = groupIndex * groupWidthPercent + dayIndexInGroup * dayWidthPercent;

                  // 加入邊距（與事件卡片一致）
                  const marginPx = CARD_SPACING.margin;

                  return (
                    <PlacementPreview
                      task={placementMode.selectedTask}
                      startTime={startTime}
                      endTime={endTime}
                      position={{
                        top,
                        left: leftPercent,
                        width: dayWidthPercent,
                        height,
                        usePercent: true,
                        marginPx,
                      }}
                      onClick={onConfirmPlacement}
                    />
                  );
                })()}

              {/* 跨日期拖拽預覽卡片 */}
              {dragState &&
                dragState.currentDayIndex !== undefined &&
                dragState.originalDayIndex !== undefined &&
                dragState.currentDayIndex !== dragState.originalDayIndex && (() => {
                  // 找到被拖拽的項目
                  const draggedItem = scheduleItems.find(item => item.id === dragState.itemId);
                  if (!draggedItem) return null;

                  // 計算目標日期在哪個區間和哪一天
                  const targetDayIndexInCurrentGroup = dragState.currentDayIndex;

                  // 目前只支援在 current 區間內拖動（3 天之間）
                  // 檢查目標日期是否在 current 區間（0, 1, 2）
                  if (targetDayIndexInCurrentGroup < 0 || targetDayIndexInCurrentGroup >= DAYS_TO_SHOW) {
                    return null;
                  }

                  // 使用拖拽狀態中的時間計算位置
                  const startTimeHour = dragState.currentStartTime.getHours();
                  const startTimeMinute = dragState.currentStartTime.getMinutes();
                  const endTimeHour = dragState.currentEndTime.getHours();
                  const endTimeMinute = dragState.currentEndTime.getMinutes();

                  const previewTop =
                    (startTimeHour - DISPLAY_CONFIG.START_HOUR) * DISPLAY_CONFIG.HOUR_HEIGHT +
                    (startTimeMinute / 60) * DISPLAY_CONFIG.HOUR_HEIGHT;
                  const previewHeight =
                    (endTimeHour - startTimeHour) * DISPLAY_CONFIG.HOUR_HEIGHT +
                    ((endTimeMinute - startTimeMinute) / 60) * DISPLAY_CONFIG.HOUR_HEIGHT;

                  // 計算水平位置（在 300% 寬度容器內）
                  // current 區間佔 33.333%，從 33.333% 開始
                  // 每天佔 33.333% / 3 = 11.111%
                  const groupWidthPercent = 100 / 3;  // 33.333%
                  const dayWidthPercent = groupWidthPercent / DAYS_TO_SHOW; // 11.111%
                  const currentGroupStartPercent = groupWidthPercent; // 33.333% (跳過 prev 區間)
                  const leftPercent = currentGroupStartPercent + targetDayIndexInCurrentGroup * dayWidthPercent;

                  const marginPx = CARD_SPACING.margin;
                  const verticalGap = CARD_SPACING.vertical;

                  const styleClasses = draggedItem.itemType === 'timebox'
                    ? getTimeBoxStyleClasses(draggedItem as TimeBox)
                    : getEventStyleClasses(draggedItem as Event);

                  return (
                    <div
                      key={`drag-preview-${draggedItem.id}`}
                      className={`absolute px-2 py-1 overflow-hidden ${styleClasses} ring-2 ring-sky-400 ring-opacity-50 z-drag-active pointer-events-none`}
                      style={{
                        top: `${previewTop + verticalGap}px`,
                        left: `${leftPercent}%`,
                        height: `${Math.max(previewHeight, DISPLAY_CONFIG.MIN_TIMEBOX_HEIGHT) - verticalGap}px`,
                        width: `${dayWidthPercent}%`,
                        marginLeft: `${marginPx}px`,
                        marginRight: `${marginPx}px`,
                        borderRadius: `${CARD_SPACING.borderRadius}px`,
                        opacity: 0.9,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        cursor: 'grabbing',
                      }}
                    >
                      <div className="text-xs font-medium truncate">{getItemDisplayTitle(draggedItem as ScheduleItem)}</div>
                      <div className="text-xs text-gray-600">
                        {`${moment(dragState.currentStartTime).format('HH:mm')} - ${moment(dragState.currentEndTime).format('HH:mm')}`}
                      </div>
                      {draggedItem.location && (
                        <div className="text-xs text-gray-600 truncate flex items-center gap-1">
                          <MapPin size={12} className="flex-shrink-0" />
                          <span className="truncate">{draggedItem.location}</span>
                        </div>
                      )}
                      {draggedItem.itemType === 'timebox' && draggedItem.taskId && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-sky-100 text-sky-800">
                            Task
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
            {/* 結束：滑動內容容器（300% 寬度）*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeDayView;